import React, { useState } from 'react';
import { Copy, Check, Info, Bookmark, Share2, Sparkles } from 'lucide-react';

interface FinancialResultCardProps {
  primaryLabel: string;
  primaryValue: string;
  primaryUnit?: string;
  secondaryMetrics?: { label: string; value: string; highlight?: boolean }[];
  lastUpdated?: string;
  jurisdictionBadge?: string;
  onOpenMethodology?: () => void;
  onSaveCalculation?: () => void;
  isSaved?: boolean;
}

export const FinancialResultCard: React.FC<FinancialResultCardProps> = ({
  primaryLabel,
  primaryValue,
  primaryUnit,
  secondaryMetrics = [],
  lastUpdated = 'Instant Client-Side',
  jurisdictionBadge = 'Certified 2026 Rules',
  onOpenMethodology,
  onSaveCalculation,
  isSaved = false,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(`${primaryLabel}: ${primaryValue} ${primaryUnit || ''}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-xs space-y-6">
      {/* Top Meta Header */}
      <div className="flex items-center justify-between border-b border-[#E5E7EB] pb-3.5">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-[#F3F4F6] text-[#1A1A1A] border border-[#E5E7EB]">
            <Sparkles className="w-3 h-3 text-[#1A1A1A]" />
            {jurisdictionBadge}
          </span>
          <span className="text-[11px] text-[#6B7280]">
            {lastUpdated}
          </span>
        </div>

        <div className="flex items-center gap-1.5">
          {onOpenMethodology && (
            <button
              onClick={onOpenMethodology}
              className="p-1.5 text-[#6B7280] hover:text-[#1A1A1A] hover:bg-[#F3F4F6] rounded-lg transition-colors text-xs flex items-center gap-1 font-medium"
              title="View Calculation Methodology"
            >
              <Info className="w-3.5 h-3.5" />
              <span className="hidden sm:inline text-[11px]">Methodology</span>
            </button>
          )}

          {onSaveCalculation && (
            <button
              onClick={onSaveCalculation}
              className={`p-1.5 rounded-lg transition-colors text-xs flex items-center gap-1 font-medium ${
                isSaved
                  ? 'text-emerald-700 bg-emerald-50'
                  : 'text-[#6B7280] hover:text-[#1A1A1A] hover:bg-[#F3F4F6]'
              }`}
              title="Save Calculation"
            >
              <Bookmark className={`w-3.5 h-3.5 ${isSaved ? 'fill-emerald-700' : ''}`} />
              <span className="hidden sm:inline text-[11px]">{isSaved ? 'Saved' : 'Save'}</span>
            </button>
          )}

          <button
            onClick={handleCopy}
            className="p-1.5 text-[#6B7280] hover:text-[#1A1A1A] hover:bg-[#F3F4F6] rounded-lg transition-colors"
            title="Copy Result"
          >
            {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {/* Numeric XL Primary Output */}
      <div className="space-y-1">
        <div className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">
          {primaryLabel}
        </div>
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-3xl sm:text-4xl font-extrabold text-[#1A1A1A] font-mono tracking-tight tabular-nums">
            {primaryValue}
          </span>
          {primaryUnit && (
            <span className="text-base font-bold text-[#6B7280] font-sans">
              {primaryUnit}
            </span>
          )}
        </div>
      </div>

      {/* Secondary Metric Grid */}
      {secondaryMetrics.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 border-t border-[#E5E7EB]">
          {secondaryMetrics.map((m, idx) => (
            <div
              key={idx}
              className={`p-3 rounded-xl border ${
                m.highlight
                  ? 'bg-emerald-50/50 border-emerald-200/80 text-emerald-950'
                  : 'bg-[#FBFBFC] border-[#E5E7EB] text-[#1A1A1A]'
              }`}
            >
              <div className="text-[11px] font-semibold text-[#6B7280]">
                {m.label}
              </div>
              <div className="text-sm font-bold font-mono mt-0.5 tabular-nums text-[#1A1A1A]">
                {m.value}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
